import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Render DOM element thành PDF blob.
 *
 * v4 — Clone DOM offscreen với kích thước CHÍNH XÁC A4
 * - Width: 210mm = 794px tại 96 DPI
 * - Padding: 15mm = 56.7px (margin in văn bản hành chính chuẩn VN)
 * - Background trắng, không shadow
 *
 * → html2canvas chụp clone → tỷ lệ pixel/mm cố định
 * → jsPDF embed image với kích thước A4 đúng
 *
 * Kết quả: PDF khớp 100% A4, content không bị co/giãn
 */

interface RenderOptions {
  scale?: number;
  format?: 'a4' | 'a5';
}

const A4_PX = 794;   // 210mm @ 96 DPI
const A4_MM_W = 210;
const A4_MM_H = 297;
const PADDING_MM = 15;  // 15mm border in văn bản hành chính

/**
 * V1.0.65: Convert form elements (textarea/input) → static text nodes trước khi chụp html2canvas.
 *
 * Bug gốc: html2canvas KHÔNG render text trong <textarea> với line break (\n) thành nhiều dòng visually,
 * vì textarea value là JS property chứ không phải child text node của DOM. Kết quả: text nhiều dòng
 * trong textarea bị render thành 1 dòng dài hoặc bị cắt.
 *
 * Fix: Tìm tất cả textarea + input trong clone, thay bằng <div> chứa text với
 * - white-space: pre-wrap để preserve \n + wrap word
 * - Style giống y textarea gốc (font, alignment, padding, ...) để không vỡ layout
 *
 * Hàm này mutate clone in-place, gọi trước khi html2canvas chụp.
 */
function convertFormElementsToStaticText(root: HTMLElement) {
  // Process textareas
  const textareas = root.querySelectorAll<HTMLTextAreaElement>('textarea');
  textareas.forEach(ta => {
    const div = document.createElement('div');
    div.textContent = ta.value;  // preserve \n in textContent
    // Copy computed style
    const cs = window.getComputedStyle(ta);
    div.style.cssText = ta.style.cssText;  // inline style first
    // Force preserve whitespace + wrap
    div.style.whiteSpace = 'pre-wrap';
    div.style.wordBreak = 'break-word';
    // Match key styles để render giống textarea gốc
    div.style.fontFamily = cs.fontFamily;
    div.style.fontSize = cs.fontSize;
    div.style.fontWeight = cs.fontWeight;
    div.style.color = cs.color;
    div.style.textAlign = cs.textAlign;
    div.style.lineHeight = cs.lineHeight;
    div.style.padding = cs.padding;
    div.style.margin = cs.margin;
    div.style.width = cs.width;
    div.style.minHeight = cs.minHeight !== '0px' ? cs.minHeight : '';
    div.style.boxSizing = cs.boxSizing;
    div.style.border = 'none';
    div.style.background = 'transparent';
    // Replace
    ta.parentNode?.replaceChild(div, ta);
  });

  // Process text inputs (single-line) - convert tương tự để giữ alignment + giá trị
  const inputs = root.querySelectorAll<HTMLInputElement>('input[type="text"], input[type="number"], input:not([type])');
  inputs.forEach(inp => {
    const div = document.createElement('div');
    div.textContent = inp.value;
    const cs = window.getComputedStyle(inp);
    div.style.cssText = inp.style.cssText;
    div.style.whiteSpace = 'pre-wrap';
    div.style.wordBreak = 'break-word';
    div.style.fontFamily = cs.fontFamily;
    div.style.fontSize = cs.fontSize;
    div.style.fontWeight = cs.fontWeight;
    div.style.color = cs.color;
    div.style.textAlign = cs.textAlign;
    div.style.lineHeight = cs.lineHeight;
    div.style.padding = cs.padding;
    div.style.margin = cs.margin;
    div.style.width = cs.width;
    div.style.boxSizing = cs.boxSizing;
    div.style.border = 'none';
    div.style.background = 'transparent';
    inp.parentNode?.replaceChild(div, inp);
  });

  // Process number inputs (Ant Design InputNumber wrapper)
  // AntD InputNumber render thành <input> bên trong wrapper - đã được xử lý ở trên
}

export async function renderElementToPdf(
  element: HTMLElement,
  options: RenderOptions = {}
): Promise<Blob> {
  const { scale = 2 } = options;

  // Bước 1: clone element offscreen để chụp với layout A4 chuẩn
  // — không phụ thuộc vào style hiện tại của element gốc trên màn hình
  const clone = element.cloneNode(true) as HTMLElement;

  // Render container offscreen
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-99999px';
  container.style.top = '0';
  container.style.background = '#ffffff';
  container.style.zIndex = '-1';

  // Set clone styling: width chính xác A4, padding 15mm, không shadow/border
  // Class pdf-export-mode trigger CSS compact (font nhỏ, ẩn buttons, padding chặt)
  clone.classList.add('pdf-export-mode');
  clone.style.width = `${A4_PX}px`;
  clone.style.maxWidth = `${A4_PX}px`;
  clone.style.minWidth = `${A4_PX}px`;
  clone.style.margin = '0';
  clone.style.padding = `${PADDING_MM}mm`;
  clone.style.boxShadow = 'none';
  clone.style.border = 'none';
  clone.style.background = '#ffffff';
  clone.style.boxSizing = 'border-box';

  container.appendChild(clone);
  document.body.appendChild(container);

  try {
    // V1.0.65: Convert textarea/input → div trước khi html2canvas chụp
    //   (textarea với line breaks không render đúng trong canvas)
    convertFormElementsToStaticText(clone);

    // Đợi browser layout xong (đảm bảo DOM render xong trước khi chụp)
    await new Promise(resolve => requestAnimationFrame(() => resolve(null)));

    // Bước 2: chụp clone
    const canvas = await html2canvas(clone, {
      scale,
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: false,
      logging: false,
      width: A4_PX,
      height: clone.offsetHeight,
      windowWidth: A4_PX,
    });

    // Bước 3: tính tỷ lệ
    // canvas.width = A4_PX × scale → tương ứng A4_MM_W = 210mm
    // pxPerMm = canvas.width / A4_MM_W
    const pxPerMm = canvas.width / A4_MM_W;
    const pageHeightPx = A4_MM_H * pxPerMm;
    const totalContentHeightMm = canvas.height / pxPerMm;

    // Bước 4: tạo PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    // Trường hợp 1: vừa 1 trang
    if (totalContentHeightMm <= A4_MM_H + 1) {
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      pdf.addImage(
        imgData,
        'JPEG',
        0, 0,
        A4_MM_W,
        Math.min(totalContentHeightMm, A4_MM_H),
        undefined,
        'FAST'
      );
      return pdf.output('blob');
    }

    // Trường hợp 2: nhiều trang
    const totalPages = Math.ceil(canvas.height / pageHeightPx);

    for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
      if (pageIdx > 0) pdf.addPage();

      const sliceY = pageIdx * pageHeightPx;
      const sliceHeight = Math.min(pageHeightPx, canvas.height - sliceY);

      // Page canvas luôn = full pageHeightPx (fill trắng phần dưới nếu slice ngắn)
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = pageHeightPx;

      const ctx = pageCanvas.getContext('2d');
      if (!ctx) throw new Error('Không tạo được canvas context');

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

      ctx.drawImage(
        canvas,
        0, sliceY,
        canvas.width, sliceHeight,
        0, 0,
        canvas.width, sliceHeight
      );

      const imgData = pageCanvas.toDataURL('image/jpeg', 0.95);

      // Image FILL FULL A4 (210 × 297mm)
      pdf.addImage(
        imgData,
        'JPEG',
        0, 0,
        A4_MM_W, A4_MM_H,
        undefined,
        'FAST'
      );
    }

    return pdf.output('blob');
  } finally {
    // Cleanup clone
    document.body.removeChild(container);
  }
}

/**
 * Helper: render rồi download trực tiếp.
 */
export async function downloadElementAsPdf(
  element: HTMLElement,
  filename: string,
  options?: RenderOptions
): Promise<void> {
  const blob = await renderElementToPdf(element, options);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/**
 * Phase B: Vị trí 1 signature slot trong PDF (đơn vị mm — chuẩn jsPDF).
 */
export interface SignaturePositionMm {
  /** Trang PDF (1-based) */
  page: number;
  /** Index của slot trong items[] */
  slotIndex: number;
  /** Label slot (debug + match với resolved signers) */
  label: string;
  /** Tọa độ tính từ góc trên trái trang */
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Phase B: Render PDF + extract vị trí các phần tử có data-signature-slot-index.
 *
 * Logic:
 * 1. Clone DOM offscreen như renderElementToPdf (cùng A4_PX width)
 * 2. Trước khi chụp html2canvas, đọc bounding rect của các marked elements
 * 3. Convert px → mm theo pxPerMm = canvas.width / 210
 * 4. Tính trang dựa trên y / pageHeightMm
 *
 * Trả về { blob: PDF, positions: [...] }
 */
export async function renderElementToPdfWithPositions(
  element: HTMLElement,
  options: RenderOptions = {}
): Promise<{ blob: Blob; positions: SignaturePositionMm[] }> {
  const { scale = 2 } = options;

  // Clone giống renderElementToPdf
  const clone = element.cloneNode(true) as HTMLElement;
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-99999px';
  container.style.top = '0';
  container.style.background = '#ffffff';
  container.style.zIndex = '-1';

  clone.classList.add('pdf-export-mode');
  clone.style.width = `${A4_PX}px`;
  clone.style.maxWidth = `${A4_PX}px`;
  clone.style.minWidth = `${A4_PX}px`;
  clone.style.margin = '0';
  clone.style.padding = `${PADDING_MM}mm`;
  clone.style.boxShadow = 'none';
  clone.style.border = 'none';
  clone.style.background = '#ffffff';
  clone.style.boxSizing = 'border-box';

  container.appendChild(clone);
  document.body.appendChild(container);

  try {
    // V1.0.65: Convert textarea/input → div TRƯỚC khi đọc bounding rect
    //   (vì signature slots cũng có thể có dynamic content, position rect cần stable)
    convertFormElementsToStaticText(clone);

    await new Promise(resolve => requestAnimationFrame(() => resolve(null)));

    // Bước 1: extract vị trí các signature slot trong clone (đơn vị px)
    const slotEls = clone.querySelectorAll<HTMLElement>('[data-signature-slot-index]');
    const cloneRect = clone.getBoundingClientRect();
    const positionsPx: Array<{ slotIndex: number; label: string; x: number; y: number; w: number; h: number }> = [];
    slotEls.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const slotIndex = parseInt(el.dataset.signatureSlotIndex ?? '0', 10);
      const label = el.dataset.signatureSlotLabel ?? '';
      positionsPx.push({
        slotIndex,
        label,
        x: rect.left - cloneRect.left,
        y: rect.top - cloneRect.top,
        w: rect.width,
        h: rect.height,
      });
    });

    // Bước 2: chụp clone
    const canvas = await html2canvas(clone, {
      scale,
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: false,
      logging: false,
      width: A4_PX,
      height: clone.offsetHeight,
      windowWidth: A4_PX,
    });

    const pxPerMm = canvas.width / A4_MM_W;
    const cssPxPerMm = pxPerMm / scale;
    const pageHeightPx = A4_MM_H * pxPerMm;
    const totalContentHeightMm = canvas.height / pxPerMm;

    // Bước 3: convert positions px → mm + xác định page
    const positions: SignaturePositionMm[] = positionsPx.map((p) => {
      const xMm = p.x / cssPxPerMm;
      const yMm = p.y / cssPxPerMm;
      const wMm = p.w / cssPxPerMm;
      const hMm = p.h / cssPxPerMm;
      const page = Math.floor(yMm / A4_MM_H) + 1;
      const yOnPage = yMm - (page - 1) * A4_MM_H;
      return {
        page,
        slotIndex: p.slotIndex,
        label: p.label,
        x: Math.round(xMm * 100) / 100,
        y: Math.round(yOnPage * 100) / 100,
        width: Math.round(wMm * 100) / 100,
        height: Math.round(hMm * 100) / 100,
      };
    });

    // Bước 4: build PDF (giống renderElementToPdf)
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    if (totalContentHeightMm <= A4_MM_H + 1) {
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      pdf.addImage(imgData, 'JPEG', 0, 0, A4_MM_W, Math.min(totalContentHeightMm, A4_MM_H), undefined, 'FAST');
      return { blob: pdf.output('blob'), positions };
    }

    const totalPages = Math.ceil(canvas.height / pageHeightPx);
    for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
      if (pageIdx > 0) pdf.addPage();
      const sliceY = pageIdx * pageHeightPx;
      const sliceHeight = Math.min(pageHeightPx, canvas.height - sliceY);
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = pageHeightPx;
      const ctx = pageCanvas.getContext('2d');
      if (!ctx) throw new Error('Không tạo được canvas context');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(canvas, 0, sliceY, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
      const imgData = pageCanvas.toDataURL('image/jpeg', 0.95);
      pdf.addImage(imgData, 'JPEG', 0, 0, A4_MM_W, A4_MM_H, undefined, 'FAST');
    }

    return { blob: pdf.output('blob'), positions };
  } finally {
    document.body.removeChild(container);
  }
}
